import React, { useCallback, useMemo, useState, KeyboardEvent } from 'react';
import { createEditor, Descendant, Element as SlateElement, Transforms, Editor, Text } from 'slate';
import { Slate, Editable, withReact, RenderLeafProps, RenderElementProps, useSlate } from 'slate-react';
import { withHistory } from 'slate-history';
import isHotkey from 'is-hotkey';
import { Box, FormHelperText, Typography, IconButton, Divider } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
  StrikethroughS,
  Code,
  FormatQuote,
  // FormatAlignLeft,
  // FormatAlignCenter,
  // FormatAlignRight,
  // FormatAlignJustify,
} from '@mui/icons-material';

interface SlateRichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  maxLength?: number;
  title?: string;
}

const HOTKEYS: { [key: string]: string } = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
  'mod+`': 'code',
};

const LIST_TYPES = ['numbered-list', 'bulleted-list'];
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify'];

export const SlateRichTextEditor: React.FC<SlateRichTextEditorProps> = ({
  value,
  onChange,
  onBlur,
  error,
  helperText,
  placeholder = 'Share your thoughts, experiences, or updates with the community...',
  maxLength = 5000,
  title,
}) => {
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);
  // Calculate character count (plain text only)
  const calculateCharacterCount = useCallback((nodes: Descendant[]): number => {
    return nodes.reduce((count, node) => {
      if (Text.isText(node)) {
        return count + node.text.length;
      }
      if (SlateElement.isElement(node) && node.children) {
        return count + calculateCharacterCount(node.children);
      }
      return count;
    }, 0);
  }, []);

  // Initialize editor value
  const initialValue: Descendant[] = useMemo(() => {
    let nodes: Descendant[];
    if (value) {
      try {
        const parsed = JSON.parse(value);
        nodes = Array.isArray(parsed) ? parsed : [{ type: 'paragraph', children: [{ text: value }] }];
      } catch {
        nodes = [{ type: 'paragraph', children: [{ text: value }] }];
      }
    } else {
      nodes = [{ type: 'paragraph', children: [{ text: '' }] }];
    }
    return nodes;
  }, [value]);

  const [characterCount, setCharacterCount] = useState(() => calculateCharacterCount(initialValue));

  const handleChange = useCallback((newValue: Descendant[]) => {
    const count = calculateCharacterCount(newValue);
    setCharacterCount(count);
    onChange(JSON.stringify(newValue));
  }, [onChange, calculateCharacterCount]);

  const renderElement = useCallback((props: RenderElementProps) => <Element {...props} />, []);
  const renderLeaf = useCallback((props: RenderLeafProps) => <Leaf {...props} />, []);

  return (
    <Box>
      {title && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {title} <span style={{ color: 'red' }}>*</span>
        </Typography>
      )}
      {!title && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          What's on your mind? <span style={{ color: 'red' }}>*</span>
        </Typography>
      )}
      <Box
        sx={{
          border: error ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: 1,
          '&:hover': {
            borderColor: error ? '#d32f2f' : 'rgba(0, 0, 0, 0.87)',
          },
          '&:focus-within': {
            borderColor: error ? '#d32f2f' : '#1976d2',
            borderWidth: 2,
          },
        }}
      >
        <Slate editor={editor} initialValue={initialValue} onChange={handleChange}>
          {/* Toolbar */}
          <Box
            sx={{
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
              padding: '8px',
              display: 'flex',
              gap: 0.5,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {/* Text Formatting */}
            <MarkButton format="bold" icon={<FormatBold />} />
            <MarkButton format="italic" icon={<FormatItalic />} />
            <MarkButton format="underline" icon={<FormatUnderlined />} />
            <MarkButton format="strikethrough" icon={<StrikethroughS />} />
            <MarkButton format="code" icon={<Code />} />

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* Headings */}
            <BlockButton format="heading-one" label="H1" />
            <BlockButton format="heading-two" label="H2" />
            <BlockButton format="heading-three" label="H3" />
            <BlockButton format="heading-four" label="H4" />
            <BlockButton format="heading-five" label="H5" />
            <BlockButton format="heading-six" label="H6" />

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* Block Types */}
            <BlockButton format="block-quote" icon={<FormatQuote />} />
            <BlockButton format="numbered-list" icon={<FormatListNumbered />} />
            <BlockButton format="bulleted-list" icon={<FormatListBulleted />} />

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* Text Alignment
            <BlockButton format="left" icon={<FormatAlignLeft />} />
            <BlockButton format="center" icon={<FormatAlignCenter />} />
            <BlockButton format="right" icon={<FormatAlignRight />} />
            <BlockButton format="justify" icon={<FormatAlignJustify />} /> */}

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* Link */}
            <LinkButton icon={<LinkIcon />} />
          </Box>

          {/* Editor */}
          <Box sx={{ padding: '14px', minHeight: 150 }}>
            <Editable
              renderElement={renderElement}
              renderLeaf={renderLeaf}
              placeholder={placeholder}
              spellCheck
              autoFocus={false}
              onBlur={onBlur}
              onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                for (const hotkey in HOTKEYS) {
                  if (isHotkey(hotkey, event as any)) {
                    event.preventDefault();
                    const mark = HOTKEYS[hotkey];
                    toggleMark(editor, mark);
                  }
                }
              }}
              style={{ minHeight: 150, outline: 'none' }}
            />
          </Box>
        </Slate>
      </Box>
      <FormHelperText error={error || characterCount > maxLength} sx={{ mx: 1.75, mt: 0.5, display: 'flex', justifyContent: 'space-between' }}>
        <span>{helperText || ''}</span>
        <span>{characterCount}/{maxLength} characters</span>
      </FormHelperText>
    </Box>
  );
};

// Helper functions
const toggleMark = (editor: Editor, format: string) => {
  const isActive = isMarkActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const isMarkActive = (editor: Editor, format: string) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format as keyof typeof marks] === true : false;
};

const toggleBlock = (editor: Editor, format: string) => {
  const isActive = isBlockActive(editor, format, TEXT_ALIGN_TYPES.includes(format as any) ? 'align' : 'type');
  const isList = LIST_TYPES.includes(format as any);

  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type as any) &&
      !TEXT_ALIGN_TYPES.includes(format as any),
    split: true,
  });

  let newProperties: Partial<SlateElement>;
  if (TEXT_ALIGN_TYPES.includes(format as any)) {
    newProperties = {
      align: isActive ? undefined : format,
    } as any;
  } else {
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    } as any;
  }
  Transforms.setNodes<SlateElement>(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] } as any;
    Transforms.wrapNodes(editor, block);
  }
};

const isBlockActive = (editor: Editor, format: string, blockType: 'type' | 'align' = 'type') => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) => {
        if (!Editor.isEditor(n) && SlateElement.isElement(n)) {
          if (blockType === 'align') {
            return (n as any).align === format;
          }
          return n.type === format;
        }
        return false;
      },
    })
  );

  return !!match;
};

// Toolbar components
const MarkButton = ({ format, icon }: { format: string; icon: React.ReactNode }) => {
  const editor = useSlate();

  return (
    <IconButton
      size="small"
      onMouseDown={(e) => {
        e.preventDefault();
        toggleMark(editor, format);
      }}
      sx={{
        color: isMarkActive(editor, format) ? 'primary.main' : 'text.secondary',
        bgcolor: isMarkActive(editor, format) ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {icon}
    </IconButton>
  );
};

const BlockButton = ({ format, icon, label }: { format: string; icon?: React.ReactNode; label?: string }) => {
  const editor = useSlate();
  const isActive = isBlockActive(editor, format, TEXT_ALIGN_TYPES.includes(format as any) ? 'align' : 'type');

  return (
    <IconButton
      size="small"
      onMouseDown={(e) => {
        e.preventDefault();
        toggleBlock(editor, format);
      }}
      sx={{
        color: isActive ? 'primary.main' : 'text.secondary',
        bgcolor: isActive ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: 'action.hover' },
        minWidth: label ? 40 : 'auto',
      }}
    >
      {icon || <Typography variant="caption" sx={{ fontWeight: 600 }}>{label}</Typography>}
    </IconButton>
  );
};

const LinkButton = ({ icon }: { icon: React.ReactNode }) => {
  const editor = useSlate();

  const insertLink = () => {
    const url = window.prompt('Enter the URL:');
    if (!url) return;

    const { selection } = editor;
    const isCollapsed = selection && selection.anchor.offset === selection.focus.offset;

    if (isCollapsed) {
      const linkText = window.prompt('Enter link text:');
      if (!linkText) return;

      const link = {
        type: 'link',
        url,
        children: [{ text: linkText }],
      } as any;
      Transforms.insertNodes(editor, link);
    } else {
      Transforms.wrapNodes(
        editor,
        { type: 'link', url, children: [] } as any,
        { split: true }
      );
    }
  };

  return (
    <IconButton
      size="small"
      onMouseDown={(e) => {
        e.preventDefault();
        insertLink();
      }}
      sx={{
        color: 'text.secondary',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {icon}
    </IconButton>
  );
};

// Render components
const Element = ({ attributes, children, element }: RenderElementProps) => {
  const style: React.CSSProperties = { textAlign: (element as any).align };

  switch (element.type) {
    case 'block-quote':
      return (
        <blockquote
          style={{
            ...style,
            borderLeft: '3px solid #ddd',
            paddingLeft: '10px',
            color: '#666',
            fontStyle: 'italic',
            margin: '10px 0',
          }}
          {...attributes}
        >
          {children}
        </blockquote>
      );
    case 'heading-one':
      return <h1 style={style} {...attributes}>{children}</h1>;
    case 'heading-two':
      return <h2 style={style} {...attributes}>{children}</h2>;
    case 'heading-three':
      return <h3 style={style} {...attributes}>{children}</h3>;
    case 'heading-four':
      return <h4 style={style} {...attributes}>{children}</h4>;
    case 'heading-five':
      return <h5 style={style} {...attributes}>{children}</h5>;
    case 'heading-six':
      return <h6 style={style} {...attributes}>{children}</h6>;
    case 'bulleted-list':
      return <ul {...attributes}>{children}</ul>;
    case 'numbered-list':
      return <ol {...attributes}>{children}</ol>;
    case 'list-item':
      return <li {...attributes}>{children}</li>;
    case 'link':
      return (
        <a
          href={(element as any).url}
          {...attributes}
          style={{ color: '#1976d2', textDecoration: 'underline' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </a>
      );
    default:
      return <p style={style} {...attributes}>{children}</p>;
  }
};

const Leaf = ({ attributes, children, leaf }: RenderLeafProps) => {
  if ((leaf as any).bold) {
    children = <strong>{children}</strong>;
  }

  if ((leaf as any).italic) {
    children = <em>{children}</em>;
  }

  if ((leaf as any).underline) {
    children = <u>{children}</u>;
  }

  if ((leaf as any).strikethrough) {
    children = <s>{children}</s>;
  }

  if ((leaf as any).code) {
    children = (
      <code
        style={{
          backgroundColor: '#f5f5f5',
          padding: '2px 4px',
          borderRadius: '3px',
          fontFamily: 'monospace',
          fontSize: '0.9em',
        }}
      >
        {children}
      </code>
    );
  }

  return <span {...attributes}>{children}</span>;
};
